'use client';

import { ButtonHTMLAttributes, useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

import classNames from 'classnames';
import { HTTPError } from 'ky';
import { useIsClient } from 'usehooks-ts';
import { useAccount, useSendTransaction } from 'wagmi';

import AngleDouble from '@uikit/icons/AngleDouble';
import OneInchColorBlack from '@uikit/icons/OneInchColorBlack';

import { useAuth } from '@shared/common/providers/AuthProvider';
import { useAuthModal } from '@shared/common/providers/GlobalModalsProvider';
import { useMixPanel } from '@shared/common/providers/MixPanelProvider';
import { wagmiConfig } from '@shared/common/providers/Web3Provider/wagmi';
import { ONE_INCH_NATIVE_NETWORK_TOKEN_ADDRESS } from '@shared/constants/magicSwap';
import displayWalletErrorToast from '@shared/helpers/displayWalletErrorToast';
import { useSwitchChain } from '@shared/helpers/web3/chain';
import { handleTransactionResult } from '@shared/helpers/web3/handleTransactionResult';
import launchAsyncToast from '@shared/helpers/web3/launchAsyncToast';
import useMagicIdUserWalletInfo from '@shared/hooks/useMagicIdUserWalletInfo';
import useMagicIdWallet from '@shared/hooks/useMagicIdWallet';

import {
  useGetOneInchNetworksQuery,
  useGetOneInchQuoteQuery,
  useGetOneInchTokenAllowanceQuery,
  useIncreaseOneInchTokenAllowanceMutation,
} from '@query/magicSwap';

import { storeSwapTx } from '@api/magicSwap';
import { MIXPANEL_EVENT_NAMES } from '@api/mixPanel';

import { BigNumber } from '@ethersproject/bignumber';
import { DEFAULT_QUOTE_REFETCH_INTERVAL } from '@magic-swap/components/pages/MagicSwap/constants';
import { Tabular } from '@ui/uikit/primitives/Tabular';
import { getErrorMessage } from '@utilities/errors/getErrorMessage';

import { MaxOutWalletAmountButton } from '../components/MaxOutWalletAmountButton';
import { SwapOperationFeeIndicator } from '../components/SwapOperationFeeIndicator';
import { SwapOperationStatusModal } from '../components/SwapOperationStatusModal';
import { SwapOperationSide, useSwapExperienceStore } from '../utilities/SwapExperienceStore';
import {
  SingleChainSwapOperation,
  useSwapOperationExecutor,
} from '../utilities/SwapTransactionExecutor';
import { useTokenBalance } from '../utilities/useTokenBalance';
import { EstimatedKarmaInfo } from './EstimatedKarmaInfo';
import { SwapSlippageControl } from './SwapSlippageControl';
import { SwapUnitControl, SwapUnitControlError } from './SwapUnitControl';

interface ErrorMessageMapping {
  pattern: RegExp;
  message: string;
  tooltip?: string;
}

const WELLKNOWN_API_ERROR_MESSSAGES: Array<ErrorMessageMapping> = [
  {
    pattern: /swap amount too small/i,
    message: 'Amount too small to swap',
  },
  {
    pattern: /token not supported/i,
    message: 'The selected pair of tokens is not available for cross-chain swap',
  },
];

const translateWellKnownErrorMessage = (message: string): Omit<ErrorMessageMapping, 'pattern'> => {
  return (
    WELLKNOWN_API_ERROR_MESSSAGES.find(({ pattern }) => pattern.test(message)) ?? {
      message: 'Unknown error',
    }
  );
};

const ActionButtonPrototype = ({
  children,
  className,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement>) => {
  return (
    <button
      {...props}
      className={classNames(
        'msq-btn msq-btn-lg w-full whitespace-nowrap msq-btn-primary',
        className,
      )}
    >
      {children}
    </button>
  );
};

const SignInActionButton = () => {
  const authModal = useAuthModal();

  const handleSignIn = useCallback(() => {
    authModal.open();
  }, [authModal]);

  return <ActionButtonPrototype onClick={handleSignIn}>Sign in</ActionButtonPrototype>;
};

interface IncreaseAllowanceActionButtonProps {
  disabled?: boolean;
}

const IncreaseAllowanceActionButton = ({ disabled }: IncreaseAllowanceActionButtonProps) => {
  const { track } = useMixPanel();
  const { user } = useAuth();

  const { source } = useSwapExperienceStore();

  const { address } = useAccount();

  const { mutateAsync: increaseAllowance } = useIncreaseOneInchTokenAllowanceMutation();
  const { refetch: refetchAllowance } = useGetOneInchTokenAllowanceQuery({
    networkId: source.network?.id,
    apiOptions:
      source.token?.id && address
        ? {
            searchParams: {
              tokenAddress: source.token?.id,
              walletAddress: address,
            },
          }
        : undefined,
  });

  const { sendTransactionAsync } = useSendTransaction();

  const handleIncreaseAllowance = async () => {
    if (!source.network || !source.token || !source.amount) {
      return;
    }

    track(MIXPANEL_EVENT_NAMES.SWAP_GIVE_PERMISSION_BUTTON_CLICK, {
      distinct_id: user?.id,
    });

    const tx = await increaseAllowance({
      networkId: source.network.id,
      options: {
        searchParams: {
          tokenAddress: source.token.id,
          amount: source.amount.toString(),
        },
      },
    });

    console.log('tx.data', tx.data);

    const hash = await sendTransactionAsync({
      ...(tx.data as any),
    });

    console.log('Transaction hash:', hash);

    if (!hash) {
      return;
    }

    const wagmiChain = wagmiConfig.chains.find(({ id }) => id === source.network?.id);

    if (!wagmiChain) {
      return;
    }

    await handleTransactionResult(hash, wagmiChain.id);

    console.log('Refetching allowance...');

    await refetchAllowance();
  };

  return (
    <ActionButtonPrototype disabled={disabled} onClick={handleIncreaseAllowance}>
      {`Give permission to swap ${source.token?.attributes.symbol}`}
    </ActionButtonPrototype>
  );
};

interface PerformSwapActionButtonProps {
  disabled?: boolean;
}

const PerformSwapActionButton = ({ disabled }: PerformSwapActionButtonProps) => {
  const { track } = useMixPanel();

  const { exportOperationSpecification, resetAmounts } = useSwapExperienceStore();

  const { source, destination, slippagePercent } = exportOperationSpecification();

  const { userWallet } = useMagicIdWallet();
  const { user } = useAuth();

  const { refetch: refetchSourceTokenBalance } = useTokenBalance({
    address: userWallet?.attributes.address,
    chainId: source?.network?.id,
    token: source?.token ?? undefined,
  });

  const { address } = useAccount();

  const { data: quote } = useGetOneInchQuoteQuery(
    {
      fromNetworkId: source.network?.id,
      toNetworkId: destination.network?.id,
      fromTokenAddress: source.token?.id,
      toTokenAddress: destination.token?.id,
      amount: source.token && source.amount ? source.amount.toString() : undefined,
      walletAddress: address,
    },
    {
      enabled: !!(
        source.network &&
        destination.network &&
        source.token &&
        destination.token &&
        source.amount
      ),
      refetchInterval: DEFAULT_QUOTE_REFETCH_INTERVAL,
    },
  );

  const { initiateSwapOperation, markSwapOperationAsCompleted, markSwapOperationAsFailed } =
    useSwapOperationExecutor();

  const { getUserWalletType } = useMagicIdUserWalletInfo();

  const { walletType, userWallet: userWalletInfo } = getUserWalletType();

  const isEnabled = useMemo(() => {
    return (
      !disabled &&
      source.amount &&
      source.network &&
      source.token &&
      destination.amount &&
      destination.network &&
      destination.token
    );
  }, [source, destination, disabled]);

  const handleSwapTokens = useCallback(async () => {
    const toastId = Date.now();

    if (!source.token) {
      console.warn('Source token not selected.');

      return;
    }

    if (!destination.token) {
      console.warn('Destination token not selected.');

      return;
    }

    try {
      if (!quote) {
        return;
      }

      track(MIXPANEL_EVENT_NAMES.SWAP_BUTTON_CLICK, {
        distinct_id: user?.id,
      });

      const { data: sourceTokenBalance } = await refetchSourceTokenBalance();

      if (!sourceTokenBalance) {
        throw new Error('Failed to update wallet balance.');
      }

      if (source.amount > sourceTokenBalance.value) {
        throw new Error(
          'Your wallet balance is below the specified amount. Please adjust the amount to swap and try again.',
        );
      }

      if (source.network.id === destination.network.id) {
        console.log('Initiating a single chain swap operation...');

        const operation = (await Promise.race([
          initiateSwapOperation({ source, destination, slippagePercent }, quote),
          launchAsyncToast({
            walletType,
            address: userWalletInfo.attributes.address,
            toastId,
            showExtraInfo: true,
            hideRetryButton: true,
            title: `Swapping ${source.token.attributes.symbol} to ${destination.token.attributes.symbol}...`,
          }),
        ])) as Awaited<SingleChainSwapOperation>;

        console.log('After initiating sigle-chain swap operation', operation);

        try {
          if (!operation.hash) {
            console.log('Dismissing toast with id:', toastId);

            toast.dismiss(toastId);
          } else {
            console.log('Storing swap transaction...');
            await storeSwapTx({
              networkId: source.network.id,
              options: {
                json: {
                  tx: operation.hash,
                  operationId: operation.id,
                },
              },
            });

            console.log(
              `Swap transaction stored, Marking swap operation with id ${operation.id} as completed...`,
            );

            markSwapOperationAsCompleted(operation.id);

            console.log('Dismissing toast with id:', toastId);
            toast.dismiss(toastId);

            resetAmounts();

            await refetchSourceTokenBalance();
          }
        } catch (error) {
          markSwapOperationAsFailed(operation.id, getErrorMessage(error));

          throw error;
        }
      } else {
        console.log('Initiating a cross-chain swap operation...');
        await initiateSwapOperation({ source, destination, slippagePercent }, quote);
      }
    } catch (error) {
      console.error('Error while swapping tokens:', error);

      displayWalletErrorToast({ error, justReturnHandledMessage: true });
    }
  }, [
    source,
    destination,
    quote,
    refetchSourceTokenBalance,
    initiateSwapOperation,
    slippagePercent,
    walletType,
    userWalletInfo.attributes.address,
    markSwapOperationAsCompleted,
    resetAmounts,
    markSwapOperationAsFailed,
  ]);

  return (
    <ActionButtonPrototype disabled={!isEnabled} onClick={handleSwapTokens}>
      Swap
    </ActionButtonPrototype>
  );
};

interface ActionButtonProps {
  disabled?: boolean;
}

const ActionButton = ({ disabled }: ActionButtonProps) => {
  const { isAuth } = useAuth();

  const { source, destination } = useSwapExperienceStore();

  const { address } = useAccount();

  const { data: allowance } = useGetOneInchTokenAllowanceQuery({
    networkId: source.network?.id,
    apiOptions:
      source.token?.id && address
        ? {
            searchParams: {
              tokenAddress: source.token?.id,
              walletAddress: address,
            },
          }
        : undefined,
    options: {
      enabled: !!(
        source.network?.id &&
        source.network?.id === destination.network?.id &&
        source.token?.id &&
        address
      ),
    },
  });

  const isEnoughAllowance = useMemo(() => {
    return (
      source.token &&
      BigNumber.from(source.amount ?? 0).lte(BigNumber.from(allowance?.data?.allowance ?? 0))
    );
  }, [source, allowance]);

  if (!isAuth) {
    return <SignInActionButton />;
  }

  if (isEnoughAllowance === false) {
    return <IncreaseAllowanceActionButton disabled={disabled} />;
  }

  return <PerformSwapActionButton disabled={disabled} />;
};

const SwapOperationMetadata = () => {
  return (
    <div className="rounded-[16px] p-[16px] bg-primary-50 border-2 border-primary-50">
      <Tabular className="tabular-uniform [&>.label]:text-neutral-400 [&>.content]:text-neutral-500">
        <Tabular.Row label="Provider">
          <OneInchColorBlack className="h-[32px]" />
        </Tabular.Row>

        <Tabular.Row label="Transaction fee">
          <SwapOperationFeeIndicator />
        </Tabular.Row>

        <Tabular.Row label="Slippage tolerance">
          <SwapSlippageControl />
        </Tabular.Row>
      </Tabular>
    </div>
  );
};

const SwapExperience = () => {
  const isClient = useIsClient();

  const { source, destination, getSource, updateSource, updateDestination } =
    useSwapExperienceStore();

  const { userWallet } = useMagicIdWallet();

  const { switchChain } = useSwitchChain();

  const { data: sourceTokenBalance } = useTokenBalance({
    address: userWallet?.attributes.address,
    chainId: source?.network?.id,
    token: source?.token ?? undefined,
  });

  const handleMaxOutWalletBalanceButtonClick = useCallback(() => {
    if (source?.token && sourceTokenBalance !== undefined) {
      updateSource({ amount: sourceTokenBalance });
    }
  }, [source?.token, sourceTokenBalance, updateSource]);

  const handleSourceTokenChange = useCallback(
    (updates: Partial<SwapOperationSide>) => {
      updateSource(updates);
    },
    [updateSource],
  );

  const handleDestinationTokenChange = useCallback(
    (updates: Partial<SwapOperationSide>) => {
      updateDestination(updates);
    },
    [updateDestination],
  );

  const handleNetworkChangedByUser = useCallback(async () => {
    if (source?.network?.id) {
      console.log('handleNetworkChangedByUser: Updating wallet network to', {
        'source?.network?.id': source?.network?.id,
      });

      await switchChain({
        destChainId: source?.network?.id,
        terminateConnectionOnError: false,
      });
    }
  }, [source?.network?.id, switchChain]);

  useEffect(() => {
    handleNetworkChangedByUser();
  }, [source?.network?.id, handleNetworkChangedByUser]);

  const { chain } = useAccount();

  const { data: networks } = useGetOneInchNetworksQuery();

  useEffect(() => {
    const currentSource = getSource();

    if (chain?.id && currentSource.network?.id !== chain?.id && networks) {
      console.log('update source because network changed externally', {
        'currentSource.network?.id': currentSource.network?.id,
        'chain?.id': chain?.id,
      });

      updateSource({
        network: networks.find(({ id }) => id === chain.id),
        token: null,
        amount: 0n,
      });
    }
  }, [getSource, chain?.id, networks, updateSource]);

  const {
    data: quote,
    isFetching: isQuoteQueryRefetching,
    error: quoteQueryError,
  } = useGetOneInchQuoteQuery(
    {
      fromNetworkId: source?.network?.id,
      toNetworkId: destination?.network?.id,
      fromTokenAddress: source?.token?.id,
      toTokenAddress: destination?.token?.id,
      amount: source?.token && source?.amount ? source.amount.toString() : undefined,
      walletAddress: userWallet?.attributes.address,
    },
    {
      enabled: !!(
        source?.network &&
        destination?.network &&
        source?.token &&
        destination?.token &&
        source?.amount
      ),
      refetchInterval: DEFAULT_QUOTE_REFETCH_INTERVAL,
    },
  );

  useEffect(() => {
    if (quoteQueryError) {
      updateDestination({
        amount: 0n,
      });
    } else {
      if (quote && destination?.token?.attributes.decimal) {
        updateDestination({
          amount: BigInt(quote.dstAmount),
        });
      }
    }
  }, [updateDestination, quote, quoteQueryError, destination?.token?.attributes.decimal]);

  const invertSwapSides = useCallback(() => {
    updateSource({ ...destination, amount: 0n });
    updateDestination({ ...source, amount: 0n });
  }, [source, destination, updateSource, updateDestination]);

  const { operations, purgeSwapOperation } = useSwapOperationExecutor();

  const handleCloseSwapOperationStatusModalClose = useCallback(() => {
    purgeSwapOperation(operations[0].id);
  }, [operations, purgeSwapOperation]);

  const { data: tokenBalance } = useTokenBalance({
    address: userWallet?.attributes.address,
    chainId: source?.network?.id,
    token: source?.token ?? undefined,
  });

  const [isBalanceExceeded, setIsBalanceExceeded] = useState(false);

  useEffect(() => {
    if (source?.amount !== undefined) {
      setIsBalanceExceeded(tokenBalance !== undefined && source.amount > tokenBalance);
    }
  }, [tokenBalance, source.amount]);

  const sourceUnitErrors = useMemo(() => {
    const errors: Array<SwapUnitControlError> = [];

    if (
      !!source.network &&
      !!destination.network &&
      source.network.id !== destination.network.id &&
      source.token?.id === ONE_INCH_NATIVE_NETWORK_TOKEN_ADDRESS
    ) {
      errors.push({
        message: `Swapping native tokens is not supported.`,
        tooltip: `Swapping native tokens is not supported. Swap ${source.token?.attributes.symbol} to wrapped ${source.token?.attributes.symbol} first and then swap wrapped ${source.token?.attributes.symbol} to ${destination.token?.attributes.symbol}.`,
      });
    }

    if (
      source?.amount !== undefined &&
      tokenBalance !== undefined &&
      source.amount > tokenBalance
    ) {
      errors.push({
        message: `There's not enough ${source?.token?.attributes.symbol} in your wallet`,
      });
    }

    return errors;
  }, [source, destination, tokenBalance]);

  const destinationUnitErrors = useMemo(() => {
    const errors: Array<SwapUnitControlError> = [];

    if (!quoteQueryError) {
      return errors;
    }

    const rawErrorMessage = getErrorMessage(quoteQueryError);

    if (quoteQueryError instanceof HTTPError) {
      errors.push(translateWellKnownErrorMessage(rawErrorMessage));
    } else {
      errors.push({ message: rawErrorMessage });
    }

    return errors;
  }, [quoteQueryError]);

  return (
    <div className="flex flex-col gap-[32px]">
      <div>
        <SwapUnitControl
          label="You pay"
          data={source}
          headerControls={
            isClient &&
            userWallet?.attributes.address && (
              <MaxOutWalletAmountButton onClick={handleMaxOutWalletBalanceButtonClick} />
            )
          }
          onChange={handleSourceTokenChange}
          error={sourceUnitErrors[0]}
        />

        <div className="flex flex-row items-center justify-center overflow-visible h-[16px]">
          <div className="flex flex-row items-center justify-center size-[60px] bg-neutral-50 rounded-full">
            <button className="flex flex-row items-center justify-center size-[48px] bg-primary-100 rounded-full">
              <AngleDouble
                onClick={invertSwapSides}
                className="icon-[24px] rotate-90 text-secondary-700"
              />
            </button>
          </div>
        </div>

        <SwapUnitControl
          label="You receive"
          data={destination}
          isReadOnly={true}
          //  isClient here prevents blinking upon initial rendering on the server
          isDataStale={isClient && isQuoteQueryRefetching}
          onChange={handleDestinationTokenChange}
          error={destinationUnitErrors[0]}
        />
      </div>

      <div className="flex flex-col gap-[32px]">
        <ActionButton
          disabled={
            isBalanceExceeded ||
            !!quoteQueryError ||
            sourceUnitErrors.length > 0 ||
            destinationUnitErrors.length > 0
          }
        />

        <SwapOperationMetadata />

        <EstimatedKarmaInfo
          estimatedKarma={quote?.estimatedKarma || 0}
          isDataStale={isClient && isQuoteQueryRefetching}
        />
      </div>

      <SwapOperationStatusModal
        open={!!operations[0]}
        operation={operations[0]}
        onClose={handleCloseSwapOperationStatusModalClose}
      />
    </div>
  );
};

export { SwapExperience };
